/**
 * Signal measurement units — shared client-side resolver.
 * ========================================================
 *
 * Signals carry an optional measurement unit ("$/MWh", "kWh", "%"), set by
 * the user from the Data Signals page or inferred by the background LLM
 * pass at import. List endpoints already return `unit` on their rows;
 * surfaces that only hold RAW KEY STRINGS (deployment forecast charts,
 * inference sparklines, overview series...) use this hook: it fetches the
 * workspace's {key: {unit, origin}} map once (GET /signal/units), caches it
 * per workspace, and resolves keys — including entity-suffixed children
 * (`base__AAPL` inherits `base`'s unit, matching the backend rule).
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { getLocalUser } from './localUser';

// Preset options for the unit picker (Autocomplete with freeSolo, so
// anything not listed can simply be typed — the "Other" affordance).
export const UNIT_PRESETS = [
  { group: 'Currency', options: ['$', '€', '£', '$/MWh', '$/bbl', '$/MMBtu', '$/t', '$/unit'] },
  { group: 'Percent & scores', options: ['%', 'bps', 'index', 'score', 'ratio'] },
  { group: 'Energy & power', options: ['kWh', 'MWh', 'GWh', 'kW', 'MW', 'GW'] },
  { group: 'Volume & flow', options: ['bbl', 'bbl/d', 'Bcf', 'Bcf/d', 'm³', 'L', 'mL', 'gal'] },
  { group: 'Mass', options: ['kg', 't', 'lb', 'oz'] },
  { group: 'Weather', options: ['°C', '°F', 'mm', 'm/s', 'W/m²', 'hPa'] },
  { group: 'Counts & time', options: ['count', 'units', 'orders', 'visits', 'days', 'hours', 'minutes'] },
];

// Flat list with group lookup for MUI Autocomplete's groupBy.
export const UNIT_PRESET_OPTIONS = UNIT_PRESETS.flatMap(
  ({ group, options }) => options.map((value) => ({ group, value })),
);

// Per-workspace cache so N components on one page share a single fetch.
const _cache = {};      // workspaceId -> {key: {unit, origin}}
const _inflight = {};   // workspaceId -> Promise

const _workspaceId = () => {
  try { return localStorage.getItem('WORKSPACE_ID') || ''; } catch (_e) { return ''; }
};

export const fetchSignalUnits = async () => {
  const ws = _workspaceId();
  if (_cache[ws]) return _cache[ws];
  if (!_inflight[ws]) {
    _inflight[ws] = axios
      .get(`${config.API_URL}/signal/units`, { headers: getLocalUser() })
      .then((res) => {
        const body = res.data?.data ?? {};
        const map = body.units && typeof body.units === 'object' ? body.units : {};
        _cache[ws] = map;
        return _cache[ws];
      })
      .catch(() => {
        _cache[ws] = {};
        return _cache[ws];
      })
      .finally(() => { delete _inflight[ws]; });
  }
  return _inflight[ws];
};

/** Invalidate the cache (call after a unit edit) so the next mount refetches. */
export const invalidateSignalUnits = () => {
  const ws = _workspaceId();
  delete _cache[ws];
};

/**
 * Resolve one key against a units map. Exact match wins; otherwise an
 * entity-suffixed child (`base__entity`) inherits its base key's unit
 * (longest base wins). Returns null when nothing matches.
 */
export const resolveUnit = (key, units) => {
  if (!key || !units) return null;
  if (units[key]) return units[key].unit;
  let bestBase = null;
  Object.keys(units).forEach((base) => {
    if (key.startsWith(`${base}__`)
        && (bestBase === null || base.length > bestBase.length)) {
      bestBase = base;
    }
  });
  return bestBase ? units[bestBase].unit : null;
};

/**
 * Hook: returns a STABLE `(key) => unit | null` for the active workspace
 * (safe in effect/memo dependency arrays). Returns null for every key until
 * the map arrives, then re-renders with units.
 */
export const useSignalUnits = () => {
  const [units, setUnits] = useState(() => _cache[_workspaceId()] || null);

  useEffect(() => {
    let alive = true;
    fetchSignalUnits().then((map) => { if (alive) setUnits(map); });
    return () => { alive = false; };
  }, []);

  return useCallback(
    (key) => resolveUnit(key, units),
    [units],
  );
};

export default useSignalUnits;
